/* VastraGharr Luxe — hero slideshow <vg-hero> (v3)
   - One clock per slide: the gold progress line IS the timer (Web Animations API), so the line and the
     slide change can never drift; it pauses on hover, focus, off-screen, hidden tab, video buffering.
   - Swipe physics on touch: rubber-band drag, velocity flick, directional hand-off between slides.
   - Video: only the active slide's video plays (phone or desktop rendition), never off screen, never
     for visitors saving data or preferring reduced motion; the slide waits for the video to start.
   - Theme editor: block select shows and holds that slide; section load/unload rebuilds cleanly. */
(() => {
  if (customElements.get('vg-hero')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const VIDEO_MAX_MS = 30000;
  const VIDEO_WAIT_MS = 4000;
  const LEAVE_MS = 950;
  const ENTER_MS = 900;
  const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  const listen = (query, handler) => {
    if (query.addEventListener) query.addEventListener('change', handler);
    else if (query.addListener) query.addListener(handler);
  };
  const unlisten = (query, handler) => {
    if (query.removeEventListener) query.removeEventListener('change', handler);
    else if (query.removeListener) query.removeListener(handler);
  };

  /* Slide clock: a paused-able timer. Uses the progress line's Web Animation when there is one,
     a detached KeyframeEffect when not, and setTimeout as the last resort. */
  class SlideClock {
    constructor(duration, fill, onDone) {
      this.duration = Math.max(500, duration);
      this.onDone = onDone;
      this.done = false;
      this.animation = null;

      const timing = { duration: this.duration, easing: 'linear', fill: 'both' };
      try {
        if (fill && typeof fill.animate === 'function') {
          this.animation = fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], timing);
        } else if (typeof window.KeyframeEffect === 'function' && typeof window.Animation === 'function') {
          this.animation = new Animation(new KeyframeEffect(null, null, timing), document.timeline);
        }
      } catch (error) {
        this.animation = null;
      }

      if (this.animation) {
        this.animation.pause();
        this.animation.onfinish = () => this.finish();
      } else {
        this.remaining = this.duration;
        this.timer = null;
      }
    }

    finish() {
      if (this.done) return;
      this.done = true;
      this.onDone();
    }

    play() {
      if (this.done) return;
      if (this.animation) {
        if (this.animation.playState !== 'running') this.animation.play();
        return;
      }
      if (this.timer) return;
      this.startedAt = performance.now();
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.finish();
      }, Math.max(0, this.remaining));
    }

    pause() {
      if (this.done) return;
      if (this.animation) {
        if (this.animation.playState === 'running') this.animation.pause();
        return;
      }
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
      this.remaining -= performance.now() - this.startedAt;
    }

    /* Change the total length but keep the elapsed time (used once a video's real length is known). */
    setDuration(duration) {
      const next = Math.max(500, duration);
      if (this.animation) {
        const effect = this.animation.effect;
        if (effect && typeof effect.updateTiming === 'function') effect.updateTiming({ duration: next });
      } else {
        const elapsed = this.duration - this.remaining;
        this.remaining = Math.max(0, next - elapsed);
        if (this.timer) {
          window.clearTimeout(this.timer);
          this.timer = null;
          this.play();
        }
      }
      this.duration = next;
    }

    cancel() {
      this.done = true;
      if (this.animation) {
        this.animation.onfinish = null;
        this.animation.cancel();
      }
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  class VgHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-vg-hero-slide]'));
      if (!this.slides.length) return;

      this.stage = this.querySelector('[data-vg-hero-stage]');
      this.dots = Array.from(this.querySelectorAll('[data-vg-hero-dot]'));
      this.pauseButton = this.querySelector('[data-vg-hero-pause]');
      this.counter = this.querySelector('[data-vg-hero-current]');
      this.progress = this.querySelector('[data-vg-hero-progress]');
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.baseDuration = Math.max(2, parseFloat(this.dataset.speed) || 5) * 1000;
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );

      if (typeof this.userPaused !== 'boolean') this.userPaused = reducedMotion.matches;
      this.hovering = false;
      this.dragging = false;
      this.focusInside = false;
      this.inView = true;
      this.editorHold = false;
      this.waitingForVideo = false;
      this.videoStalled = false;
      this.clock = null;
      this.leaveTimers = new Map();

      this.onVisibility = () => this.update();
      this.onMediaChange = () => {
        this.prepareClock();
        this.update();
      };
      this.onMotionChange = () => {
        if (reducedMotion.matches) this.userPaused = true;
        this.update();
      };
      this.onBlockSelect = (event) => this.handleBlockSelect(event, true);
      this.onBlockDeselect = (event) => this.handleBlockSelect(event, false);

      // Element listeners survive a disconnect, and the theme editor moves (not re-renders) sections on reorder.
      if (!this.vgBound) {
        this.vgBound = true;
        this.bindControls();
        this.bindGestures();
        this.bindVideos();
      }

      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      listen(mobileQuery, this.onMediaChange);
      listen(reducedMotion, this.onMotionChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting;
            });
            this.update();
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      }

      this.classList.add('is-ready');
      this.goTo(this.index, { initial: true });
    }

    disconnectedCallback() {
      if (!this.slides || !this.slides.length) return;
      if (this.clock) this.clock.cancel();
      this.clock = null;
      window.clearTimeout(this.videoWait);
      this.leaveTimers.forEach((timer) => window.clearTimeout(timer));
      this.leaveTimers.clear();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      unlisten(mobileQuery, this.onMediaChange);
      unlisten(reducedMotion, this.onMotionChange);
      this.querySelectorAll('[data-vg-hero-video]').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    motionOK() {
      return !reducedMotion.matches;
    }

    /* ---------- wiring ---------- */
    bindControls() {
      const prev = this.querySelector('[data-vg-hero-prev]');
      const next = this.querySelector('[data-vg-hero-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1, { user: true, dir: 'prev' }));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1, { user: true, dir: 'next' }));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.vgHeroDot, 10) || 0, { user: true }));
      });

      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.update();
        });
      }

      this.addEventListener('keydown', (event) => {
        if (this.slides.length < 2 || event.defaultPrevented) return;
        if (event.target.closest('input, textarea, select')) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
        const backward = rtl ? 'ArrowRight' : 'ArrowLeft';
        if (event.key === backward) {
          event.preventDefault();
          this.goTo(this.index - 1, { user: true, dir: 'prev' });
        } else if (event.key === forward) {
          event.preventDefault();
          this.goTo(this.index + 1, { user: true, dir: 'next' });
        }
      });

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovering = true;
        this.update();
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovering = false;
        this.update();
      });
      this.addEventListener('focusin', () => {
        this.focusInside = true;
        this.update();
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.focusInside = false;
          this.update();
        }
      });
    }

    bindGestures() {
      if (!this.stage || this.slides.length < 2) return;
      let startX = 0;
      let startY = 0;
      let dx = 0;
      let width = 1;
      let tracking = false;
      let horizontal = null;
      let samples = [];

      const rubber = (value) => {
        // Follows the finger at 40%, easing off the further it travels (never past ~30% of the frame).
        const limit = width * 0.3;
        const raw = value * 0.4;
        return (raw / (1 + Math.abs(raw) / limit)) || 0;
      };

      const velocity = () => {
        if (samples.length < 2) return 0;
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        return dt > 0 ? (last.x - first.x) / dt : 0;
      };

      this.stage.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse' || !event.isPrimary) return;
          tracking = true;
          horizontal = null;
          startX = event.clientX;
          startY = event.clientY;
          dx = 0;
          width = this.stage.clientWidth || window.innerWidth || 1;
          samples = [{ x: event.clientX, t: event.timeStamp }];
        },
        { passive: true }
      );

      this.stage.addEventListener(
        'pointermove',
        (event) => {
          if (!tracking) return;
          dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
            horizontal = Math.abs(dx) > Math.abs(dy) * 1.1;
            if (horizontal) {
              this.dragging = true;
              this.classList.add('is-dragging');
              this.update();
            } else {
              tracking = false;
            }
          }
          if (!horizontal) return;
          samples.push({ x: event.clientX, t: event.timeStamp });
          while (samples.length > 2 && event.timeStamp - samples[0].t > 100) samples.shift();
          this.dragOffset = rubber(dx);
          this.style.setProperty('--vg-drag', `${this.dragOffset.toFixed(1)}px`);
        },
        { passive: true }
      );

      const end = (event) => {
        if (!tracking && !this.dragging) return;
        tracking = false;
        const wasHorizontal = horizontal;
        horizontal = null;
        if (!wasHorizontal) return;

        const v = event && event.type === 'pointerup' ? velocity() : 0;
        const distance = Math.abs(dx);
        const commit = distance > Math.min(72, width * 0.18) || (Math.abs(v) > 0.45 && distance > 16);
        const offset = this.dragOffset || 0;

        this.dragging = false;
        this.dragOffset = 0;
        this.classList.remove('is-dragging');
        this.style.removeProperty('--vg-drag');

        if (commit && (!event || event.type === 'pointerup')) {
          this.suppressClick = true;
          window.setTimeout(() => {
            this.suppressClick = false;
          }, 400);
          const sign = (Math.abs(v) > 0.45 ? v : dx) < 0 ? 1 : -1;
          const rtl = getComputedStyle(this).direction === 'rtl';
          const step = rtl ? -sign : sign;
          this.goTo(this.index + step, { user: true, dir: sign > 0 ? 'next' : 'prev', fromOffset: offset });
        } else {
          this.update();
        }
      };

      this.stage.addEventListener('pointerup', end);
      this.stage.addEventListener('pointercancel', end);
      this.stage.addEventListener(
        'click',
        (event) => {
          if (this.suppressClick) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
      // No native image drag ghosts while swiping
      this.stage.addEventListener('dragstart', (event) => event.preventDefault());
    }

    bindVideos() {
      this.querySelectorAll('[data-vg-hero-video]').forEach((video) => {
        video.muted = true;
        video.addEventListener('playing', () => {
          video.classList.add('is-playing');
          if (video !== this.activeVideo()) return;
          this.videoStalled = false;
          if (this.waitingForVideo) this.resolveVideoWait(video);
          else this.update();
        });
        video.addEventListener('waiting', () => {
          if (video !== this.activeVideo() || this.waitingForVideo) return;
          this.videoStalled = true;
          this.update();
        });
        video.addEventListener('error', () => {
          if (video !== this.activeVideo()) return;
          this.videoStalled = false;
          if (this.waitingForVideo) {
            this.waitingForVideo = false;
            window.clearTimeout(this.videoWait);
          }
          this.update();
        });
      });
    }

    /* ---------- slide changes ---------- */
    goTo(target, options = {}) {
      const count = this.slides.length;
      const nextIndex = ((target % count) + count) % count;
      const previous = this.slides[this.index];
      const next = this.slides[nextIndex];
      const changed = previous !== next;

      let dir = options.dir;
      if (!dir) dir = nextIndex < this.index ? 'prev' : 'next';
      this.dataset.dir = dir;

      if (previous && changed && !options.initial) this.leave(previous, dir, options.fromOffset || 0);

      this.slides.forEach((slide, i) => {
        const active = i === nextIndex;
        if (active && slide.classList.contains('is-leaving')) this.clearLeave(slide);
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.inert = true;
        }
        slide.querySelectorAll('[data-vg-hero-link]').forEach((link) => {
          if (active) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        });
      });

      if (changed && !options.initial) this.enter(next, dir);

      this.dots.forEach((dot, i) => {
        const active = i === nextIndex;
        dot.classList.toggle('is-active', active);
        dot.classList.toggle('is-viewed', i < nextIndex);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.counter) this.counter.textContent = ROMAN[nextIndex] || String(nextIndex + 1);
      this.style.setProperty('--vg-hero-step', String(nextIndex + 1));

      this.index = nextIndex;
      this.armNeighbours();
      this.prepareClock();
      this.update();
    }

    /* Outgoing slide stays visible underneath and drifts away in the swipe direction while the next fades in. */
    leave(slide, dir, fromOffset) {
      this.clearLeave(slide);
      slide.classList.add('is-leaving');
      if (this.motionOK()) {
        const media = slide.querySelector('[data-vg-hero-media]');
        if (media && typeof media.animate === 'function') {
          const to = dir === 'next' ? '-6%' : '6%';
          slide.vgLeaveAnimation = media.animate(
            [{ transform: `translate3d(${fromOffset}px, 0, 0)` }, { transform: `translate3d(${to}, 0, 0)` }],
            { duration: LEAVE_MS, easing: EASE, fill: 'forwards' }
          );
        }
      }
      this.leaveTimers.set(
        slide,
        window.setTimeout(() => this.clearLeave(slide), LEAVE_MS)
      );
    }

    clearLeave(slide) {
      const timer = this.leaveTimers.get(slide);
      if (timer) window.clearTimeout(timer);
      this.leaveTimers.delete(slide);
      slide.classList.remove('is-leaving');
      if (slide.vgLeaveAnimation) {
        slide.vgLeaveAnimation.cancel();
        slide.vgLeaveAnimation = null;
      }
    }

    enter(slide, dir) {
      if (!this.motionOK()) return;
      const col = slide.querySelector('[data-vg-hero-media-col]');
      if (!col || typeof col.animate !== 'function') return;
      const from = dir === 'next' ? '3%' : '-3%';
      col.animate([{ transform: `translate3d(${from}, 0, 0)` }, { transform: 'translate3d(0, 0, 0)' }], {
        duration: ENTER_MS,
        easing: EASE,
      });
    }

    armNeighbours() {
      const count = this.slides.length;
      const keep = new Set([this.index, (this.index + 1) % count, (this.index - 1 + count) % count]);
      this.slides.forEach((slide, i) => slide.classList.toggle('is-armed', keep.has(i)));
    }

    /* ---------- timing ---------- */
    activeVideo() {
      const slide = this.slides[this.index];
      if (!slide) return null;
      const videos = Array.from(slide.querySelectorAll('[data-vg-hero-video]'));
      const wanted = mobileQuery.matches ? 'mobile' : 'desktop';
      return (
        videos.find((video) => video.dataset.vgHeroVideo === wanted) ||
        videos.find((video) => video.dataset.vgHeroVideo === 'all') ||
        null
      );
    }

    canPlayVideo() {
      return !saveData() && !reducedMotion.matches;
    }

    prepareClock() {
      if (this.clock) this.clock.cancel();
      this.clock = null;
      window.clearTimeout(this.videoWait);
      this.waitingForVideo = false;
      this.videoStalled = false;
      if (!this.autoplay || this.userPaused) return;

      const forIndex = this.index;
      this.clock = new SlideClock(this.baseDuration, this.progress, () => {
        if (this.index === forIndex) this.goTo(this.index + 1, { dir: 'next' });
      });

      const video = this.canPlayVideo() ? this.activeVideo() : null;
      if (!video) return;
      if (video.readyState >= 3 && !video.paused) {
        this.resolveVideoWait(video);
        return;
      }
      this.waitingForVideo = true;
      this.videoWait = window.setTimeout(() => {
        if (!this.waitingForVideo) return;
        this.waitingForVideo = false;
        this.update();
      }, VIDEO_WAIT_MS);
    }

    resolveVideoWait(video) {
      window.clearTimeout(this.videoWait);
      this.waitingForVideo = false;
      const length = video.duration && isFinite(video.duration) ? video.duration * 1000 : 0;
      const duration = Math.min(VIDEO_MAX_MS, Math.max(this.baseDuration, length));
      if (this.clock) this.clock.setDuration(duration);
      this.update();
    }

    shouldRun() {
      return (
        this.autoplay &&
        !this.userPaused &&
        !this.hovering &&
        !this.dragging &&
        !this.focusInside &&
        !this.editorHold &&
        !this.waitingForVideo &&
        !this.videoStalled &&
        this.inView &&
        !document.hidden
      );
    }

    update() {
      if (!this.slides || !this.slides.length) return;
      // An explicit pause (button / reduced motion) drops the timer so the line shows the position (II of V);
      // hover, focus, scrolling away or buffering only freeze it.
      if (this.userPaused && this.clock) {
        this.clock.cancel();
        this.clock = null;
        window.clearTimeout(this.videoWait);
        this.waitingForVideo = false;
      } else if (!this.userPaused && !this.clock && this.autoplay) {
        this.prepareClock();
      }
      const running = this.shouldRun();
      if (this.clock) {
        if (running) this.clock.play();
        else this.clock.pause();
      }
      this.classList.toggle('is-running', running);
      this.classList.toggle('is-user-paused', this.userPaused);

      if (this.pauseButton) {
        const label = this.userPaused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause;
        if (label) this.pauseButton.setAttribute('aria-label', label);
        this.pauseButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      }
      if (this.stage) {
        this.stage.setAttribute('aria-live', this.autoplay && !this.userPaused ? 'off' : 'polite');
      }
      this.syncVideos();
    }

    syncVideos() {
      const active = this.canPlayVideo() ? this.activeVideo() : null;
      const allowPlay = this.inView && !document.hidden && !this.userPaused;
      this.querySelectorAll('[data-vg-hero-video]').forEach((video) => {
        if (video === active && allowPlay) {
          if (!video.paused) return;
          video.muted = true;
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(() => {
              // Autoplay refused (e.g. low-power mode): keep the poster and let the slides move on.
              if (video !== this.activeVideo()) return;
              this.videoStalled = false;
              if (this.waitingForVideo) {
                this.waitingForVideo = false;
                window.clearTimeout(this.videoWait);
              }
              this.update();
            });
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    /* ---------- theme editor ---------- */
    handleBlockSelect(event, selected) {
      const slide = event.target && event.target.closest ? event.target.closest('[data-vg-hero-slide]') : null;
      if (!slide || !this.contains(slide)) return;
      this.editorHold = selected;
      if (selected) {
        const i = this.slides.indexOf(slide);
        if (i > -1) this.goTo(i, { user: true });
      } else {
        this.update();
      }
    }
  }

  customElements.define('vg-hero', VgHero);
})();
